import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash } from "@phosphor-icons/react";

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [depts, setDepts] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [teamOpen, setTeamOpen] = useState(false);
  const [form, setForm] = useState({ name: "", department_id: "", admin_id: "" });

  const load = async () => {
    try {
      const [t, d, u] = await Promise.all([
        api.get("/teams"), api.get("/departments"), api.get("/users"),
      ]);
      setTeams(t.data || []);
      setDepts(d.data || []);
      setAdmins((u.data || []).filter((x) => x.role === "admin" || x.role === "super_admin"));
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const addDept = async () => {
    if (!newDept.trim()) return;
    try { await api.post("/departments", { name: newDept.trim() }); setNewDept(""); toast.success("Department added"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const delDept = async (id) => {
    try { await api.delete(`/departments/${id}`); toast.success("Department removed"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const createTeam = async () => {
    if (!form.name.trim()) { toast.error("Team name is required"); return; }
    try { await api.post("/teams", form); toast.success("Team created"); setTeamOpen(false); setForm({ name: "", department_id: "", admin_id: "" }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const delTeam = async (id) => {
    try { await api.delete(`/teams/${id}`); toast.success("Team deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="teams-page">
      <PageHeader overline="Hierarchy Management" title="Departments & Teams"
        subtitle="Create departments, teams and assign team admins"
        actions={<Button onClick={() => setTeamOpen(true)} className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="new-team-btn"><Plus size={16} className="mr-1" />New Team</Button>} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Departments */}
        <div className="bg-white border border-[#E5E5E5]">
          <div className="px-4 py-3 border-b border-[#E5E5E5] font-head font-semibold">Departments</div>
          <div className="p-4 space-y-2">
            <div className="flex gap-2">
              <Input placeholder="New department" value={newDept} onChange={(e) => setNewDept(e.target.value)} className="rounded-none" data-testid="new-dept-input" />
              <Button onClick={addDept} className="rounded-none bg-[#1F4E8C] text-white" data-testid="add-dept-btn"><Plus size={16} /></Button>
            </div>
            {depts.map((d) => (
              <div key={d.id} className="flex items-center justify-between border border-[#F0F0EE] px-3 py-2 text-sm" data-testid={`dept-row-${d.id}`}>
                <span>{d.name}</span>
                <button onClick={() => delDept(d.id)} className="text-neutral-400 hover:text-red-600"><Trash size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Teams */}
        <div className="bg-white border border-[#E5E5E5] lg:col-span-2">
          <div className="px-4 py-3 border-b border-[#E5E5E5] font-head font-semibold">Teams</div>
          <div className="p-4 grid sm:grid-cols-2 gap-3">
            {teams.map((t) => (
              <div key={t.id} className="border border-[#E5E5E5] p-4" data-testid={`team-card-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div className="font-semibold">{t.name}</div>
                  <button onClick={() => delTeam(t.id)} className="text-neutral-400 hover:text-red-600"><Trash size={14} /></button>
                </div>
                <div className="text-xs text-neutral-500 mt-1">{t.department_name || "No department"}</div>
                <div className="text-sm mt-2"><span className="eyebrow text-neutral-400 mr-1">Admin:</span>{t.admin_name || "—"}</div>
                <div className="text-xs text-neutral-500 mt-1">{t.member_count} member(s)</div>
              </div>
            ))}
            {teams.length === 0 && <div className="text-sm text-neutral-500">No teams yet.</div>}
          </div>
        </div>
      </div>

      <Dialog open={teamOpen} onOpenChange={setTeamOpen}>
        <DialogContent className="rounded-none" data-testid="team-dialog">
          <DialogHeader><DialogTitle className="font-head">New Team</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><div className="eyebrow mb-1">Team Name</div><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="team-name-input" /></div>
            <div>
              <div className="eyebrow mb-1">Department</div>
              <select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="w-full h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="team-dept-select">
                <option value="">—</option>
                {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <div className="eyebrow mb-1">Team Admin</div>
              <select value={form.admin_id} onChange={(e) => setForm({ ...form, admin_id: e.target.value })} className="w-full h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="team-admin-select">
                <option value="">—</option>
                {admins.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.role})</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={createTeam} className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="save-team-btn">Create Team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
